import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { X, AlertCircle } from 'lucide-react';
import { MusikkKategori } from '@/services/api';

interface SongCategoriesInputProps {
  categories: string[];
  categoryInput: string;
  showKategoriDropdown: boolean;
  newKategoriWarning: string | null;
  filteredKategorier: MusikkKategori[];
  onCategoryInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCategoryInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onCategoryInputFocus: () => void;
  onCategoryInputBlur: () => void;
  onCategorySelect: (kategori: MusikkKategori) => void;
  onConfirmNewCategory: () => void;
  onRemoveCategory: (category: string) => void;
  onCancelNewCategory: () => void;
}

export const SongCategoriesInput: React.FC<SongCategoriesInputProps> = ({
  categories,
  categoryInput,
  showKategoriDropdown,
  newKategoriWarning,
  filteredKategorier,
  onCategoryInputChange,
  onCategoryInputKeyDown,
  onCategoryInputFocus,
  onCategoryInputBlur,
  onCategorySelect,
  onConfirmNewCategory,
  onRemoveCategory,
  onCancelNewCategory,
}) => {
  return (
    <div className="space-y-2">
      <Label htmlFor="categories">Kategorier</Label>
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <Badge
              key={category}
              className="bg-blue-100 text-blue-700 border-blue-200"
            >
              {category}
              <X 
                className="ml-1 h-3 w-3 cursor-pointer hover:opacity-70" 
                onClick={() => onRemoveCategory(category)}
              />
            </Badge>
          ))}
        </div>
        
        {/* Input med autocomplete */}
        <div className="relative">
          <Input
            id="categories"
            placeholder="Søk eller skriv inn kategori..."
            value={categoryInput}
            onChange={onCategoryInputChange}
            onKeyDown={onCategoryInputKeyDown}
            onFocus={onCategoryInputFocus}
            onBlur={onCategoryInputBlur}
            autoComplete="off"
            className="bg-slate-50/50 focus:bg-white"
          />

          {/* Dropdown med eksisterende kategorier */}
          {showKategoriDropdown && filteredKategorier.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
              {filteredKategorier.map((kat) => (
                <button
                  key={kat.id}
                  type="button"
                  onClick={() => onCategorySelect(kat)}
                  className="w-full text-left px-3 py-2 hover:bg-slate-100 text-sm"
                >
                  {kat.navn}
                </button>
              ))}
            </div>
          )}

          {/* Advarsel om ny kategori */}
          {newKategoriWarning && (
            <div className="mt-2 flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
              <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="flex-1 text-sm text-amber-800">
                <p>{newKategoriWarning}</p>
                <div className="flex gap-2 mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onCancelNewCategory}
                    className="h-7"
                  >
                    Avbryt
                  </Button>
                  <Button
                    size="sm"
                    onClick={onConfirmNewCategory}
                    className="h-7"
                  >
                    Bekreft
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <p className="text-xs text-muted-foreground">
          Trykk Enter for å legge til. Eksisterende kategorier velges automatisk ved match.
        </p>
      </div>
    </div>
  );
};

